import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import {
  getOrderIssues, resolveOrderIssue, cancelOrder,
  getVerificationOverrides, decideVerificationOverride
} from "../../api/client";
import { listenForAdminIssues } from "../../api/socket";
import LoadingIndicator from "../../components/LoadingIndicator";

const REASON_LABELS = {
  package_size: "Package size/dimensions",
  route_change: "Change in route/instructions",
  prohibited_item: "Prohibited item",
  others: "Other reason",
};

const TABS = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "escalated_cancelled", label: "Cancelled" },
  { value: "all", label: "All" },
];

const OVERRIDE_TABS = [
  { value: "requested", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" },
  { value: "all", label: "All" },
];

function IssueReports() {
  const [tab, setTab] = useState("open");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await getOrderIssues(status);
      setOrders(data);
    } catch {
      setError("Failed to load issues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);
  useEffect(() => {
    const unsubscribe = listenForAdminIssues(() => { if (tab === "open") load("open"); });
    return unsubscribe;
  }, [tab]);

  const handleResolve = async (orderId) => {
    setError("");
    setBusyId(orderId);
    try {
      await resolveOrderIssue(orderId, notes[orderId] || "");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resolve issue");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelOrder = async (orderId) => {
    setError("");
    setBusyId(orderId);
    try {
      await cancelOrder(orderId, "others", notes[orderId] || "Cancelled by Support to resolve a dispatcher-raised issue.");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to cancel order");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="role-tabs" style={{ marginBottom: "20px" }}>
        {TABS.map(t => (
          <button key={t.value} className={`role-tab ${tab === t.value ? "active" : ""}`} onClick={() => setTab(t.value)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading issues..." />
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <span className="empty-state-icon">✅</span>
          <p className="empty-state-text">{tab === "open" ? "No open issues — all clear." : "Nothing here yet."}</p>
        </div>
      ) : (
        orders.map((o) => (
          <div key={o._id} className="rider-card" style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div>
                <strong>Order #{o._id.slice(-6).toUpperCase()}</strong>
                <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                  Raised: {new Date(o.issue.raisedAt).toLocaleString()}
                </p>
              </div>
              <span className={`status-badge ${o.issue.status === "open" ? "warning" : o.issue.status === "escalated_cancelled" ? "danger" : ""}`}>
                {o.issue.status === "escalated_cancelled" ? "CANCELLED" : o.issue.status.toUpperCase()}
              </span>
            </div>

            <div className="info-row">
              <span className="label">Customer</span>
              <span className="value">{o.user?.name} ({o.user?.phone})</span>
            </div>
            <div className="info-row">
              <span className="label">Dispatcher</span>
              <span className="value">{o.rider?.name} ({o.rider?.phone})</span>
            </div>
            <div className="info-row">
              <span className="label">Reason</span>
              <span className="value">{REASON_LABELS[o.issue.reason] || o.issue.reason}</span>
            </div>
            {o.issue.note && (
              <div className="info-row">
                <span className="label">Dispatcher Note</span>
                <span className="value">{o.issue.note}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Order Status</span>
              <span className="value">{o.status.replace(/_/g, " ")}</span>
            </div>
            <div className="info-row">
              <span className="label">Route</span>
              <span className="value">{o.pickup.address.slice(0, 30)}... → {o.dropoff.address.slice(0, 30)}...</span>
            </div>

            {o.issue.status !== "open" ? (
              <div className="info-row" style={{ marginTop: "8px" }}>
                <span className="label">Resolution</span>
                <span className="value">
                  {o.issue.resolutionNote || "(no note)"} — {new Date(o.issue.resolvedAt).toLocaleString()}
                </span>
              </div>
            ) : (
              <div style={{ marginTop: "12px" }}>
                <input
                  type="text"
                  placeholder="Resolution note (what did the customer say?)"
                  value={notes[o._id] || ""}
                  onChange={e => setNotes({ ...notes, [o._id]: e.target.value })}
                  style={{ marginBottom: "8px" }}
                />
                <div className="row">
                  <button
                    className="success"
                    disabled={busyId === o._id}
                    onClick={() => handleResolve(o._id)}>
                    {busyId === o._id ? "…" : "Resolve — Order Continues"}
                  </button>
                  <button
                    className="danger"
                    disabled={busyId === o._id}
                    onClick={() => handleCancelOrder(o._id)}>
                    {busyId === o._id ? "…" : "Cancel Order"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </>
  );
}

function VerificationOverrides() {
  const [tab, setTab] = useState("requested");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await getVerificationOverrides(status);
      setOrders(data);
    } catch {
      setError("Failed to load verification override requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const handleDecide = async (orderId, approve) => {
    setError("");
    setBusyId(orderId);
    try {
      await decideVerificationOverride(orderId, approve, notes[orderId] || "");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to record decision");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <p className="hint" style={{ marginBottom: "16px" }}>
        A rider requests this after a failed PIN attempt — call the customer/receiver
        to confirm before approving. Approving lets that one delivery through without
        a matching PIN; it does not disable verification for any other order.
      </p>

      <div className="role-tabs" style={{ marginBottom: "20px" }}>
        {OVERRIDE_TABS.map(t => (
          <button key={t.value} className={`role-tab ${tab === t.value ? "active" : ""}`} onClick={() => setTab(t.value)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading requests..." />
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <span className="empty-state-icon">✅</span>
          <p className="empty-state-text">{tab === "requested" ? "No pending override requests." : "Nothing here yet."}</p>
        </div>
      ) : (
        orders.map((o) => (
          <div key={o._id} className="rider-card" style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div>
                <strong>Order #{o._id.slice(-6).toUpperCase()}</strong>
                <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                  Requested: {new Date(o.verificationOverride.requestedAt).toLocaleString()}
                </p>
              </div>
              <span className={`status-badge ${o.verificationOverride.status === "requested" ? "warning" : o.verificationOverride.status === "denied" ? "danger" : ""}`}>
                {o.verificationOverride.status.toUpperCase()}
              </span>
            </div>

            <div className="info-row">
              <span className="label">Customer</span>
              <span className="value">{o.user?.name} ({o.user?.phone})</span>
            </div>
            <div className="info-row">
              <span className="label">Dispatcher</span>
              <span className="value">{o.rider?.name} ({o.rider?.phone})</span>
            </div>
            {o.verificationOverride.note && (
              <div className="info-row">
                <span className="label">Dispatcher Note</span>
                <span className="value">{o.verificationOverride.note}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Order Status</span>
              <span className="value">{o.status.replace(/_/g, " ")}</span>
            </div>
            <div className="info-row">
              <span className="label">Route</span>
              <span className="value">{o.pickup.address.slice(0, 30)}... → {o.dropoff.address.slice(0, 30)}...</span>
            </div>

            {o.verificationOverride.status !== "requested" ? (
              <div className="info-row" style={{ marginTop: "8px" }}>
                <span className="label">Decision</span>
                <span className="value">
                  {o.verificationOverride.decisionNote || "(no note)"} — {new Date(o.verificationOverride.decidedAt).toLocaleString()}
                </span>
              </div>
            ) : (
              <div style={{ marginTop: "12px" }}>
                <input
                  type="text"
                  placeholder="Decision note (what did the customer say?)"
                  value={notes[o._id] || ""}
                  onChange={e => setNotes({ ...notes, [o._id]: e.target.value })}
                  style={{ marginBottom: "8px" }}
                />
                <div className="row">
                  <button
                    className="danger"
                    disabled={busyId === o._id}
                    onClick={() => handleDecide(o._id, false)}>
                    {busyId === o._id ? "…" : "Deny"}
                  </button>
                  <button
                    className="success"
                    disabled={busyId === o._id}
                    onClick={() => handleDecide(o._id, true)}>
                    {busyId === o._id ? "…" : "Approve Override"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </>
  );
}

export default function AdminIssues() {
  const [view, setView] = useState("issues"); // "issues" | "overrides"

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

      <div className="brand-header">
        <span className="brand-logo">🚨</span>
        <span className="brand-name">Issues</span>
        <p className="brand-tagline">Dispatcher-raised issues and PIN verification overrides</p>
      </div>

      <div className="row" style={{ marginBottom: "20px" }}>
        <button className={view === "issues" ? "" : "secondary"} onClick={() => setView("issues")}>
          🚨 Issue Reports
        </button>
        <button className={view === "overrides" ? "" : "secondary"} onClick={() => setView("overrides")}>
          🔑 Verification Overrides
        </button>
      </div>

      {view === "issues" ? <IssueReports /> : <VerificationOverrides />}
      </div>
    </div>
  );
}
